import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  People as PeopleIcon,
  Task as TaskIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompletedIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { ProjectAnalyticsService, ProjectMetrics, ProjectData } from '../../services/ProjectAnalyticsService';

// ProjectMetrics and ProjectData interfaces now imported from ProjectAnalyticsService

interface MetricCard {
  title: string;
  value: string | number;
  change: number;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'warning' | 'error';
}

/**
 * Project Analytics Component for Project Manager
 * Displays comprehensive project metrics, charts, and performance indicators
 */
const ProjectAnalytics: React.FC = () => {
  const [metrics, setMetrics] = useState<ProjectMetrics | null>(null);
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState('month');
  const [selectedProject, setSelectedProject] = useState('all');

  useEffect(() => {
    loadAnalyticsData();
  }, [timeframe, selectedProject]);

  const loadAnalyticsData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Load real analytics data from API
      const data = await ProjectAnalyticsService.getAnalytics({
        timeframe: timeframe as 'week' | 'month' | 'quarter' | 'year',
        project_id: selectedProject
      });
      
      setMetrics(data.metrics);
      setProjects(data.projects);
    } catch (err) {
      setError('Failed to load analytics data. Please try again.');
      console.error('Error loading analytics data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'active': return 'primary';
      case 'delayed': return 'error';
      case 'on-hold': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CompletedIcon color="success" />;
      case 'active': return <TaskIcon color="primary" />;
      case 'delayed': return <ErrorIcon color="error" />;
      case 'on-hold': return <WarningIcon color="warning" />;
      default: return <TaskIcon />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value}%`;
  };

  const getMetricCards = (): MetricCard[] => {
    if (!metrics) return [];

    return [
      {
        title: 'Total Projects',
        value: metrics.totalProjects,
        change: 12,
        icon: <AssessmentIcon />,
        color: 'primary'
      },
      {
        title: 'Active Projects',
        value: metrics.activeProjects,
        change: 8,
        icon: <TaskIcon />,
        color: 'primary'
      },
      {
        title: 'On-Time Delivery',
        value: formatPercentage(metrics.onTimeDelivery),
        change: 5,
        icon: <ScheduleIcon />,
        color: 'success'
      },
      {
        title: 'Budget Utilization',
        value: formatPercentage(metrics.budgetUtilization),
        change: -3,
        icon: <MoneyIcon />,
        color: 'warning'
      },
      {
        title: 'Team Productivity',
        value: formatPercentage(metrics.teamProductivity),
        change: 15,
        icon: <PeopleIcon />,
        color: 'success'
      },
      {
        title: 'Client Satisfaction',
        value: formatPercentage(metrics.clientSatisfaction),
        change: 7,
        icon: <TrendingUpIcon />,
        color: 'success'
      }
    ];
  };

  const filteredProjects = selectedProject === 'all' 
    ? projects 
    : projects.filter(p => p.id.toString() === selectedProject);

  const handleExportReport = async () => {
    try {
      const blob = await ProjectAnalyticsService.exportReport({
        timeframe,
        project_id: selectedProject,
        format: 'pdf'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `project-analytics-${timeframe}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting report:', err);
      setError('Failed to export report. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Project Analytics
        </Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Timeframe</InputLabel>
            <Select
              value={timeframe}
              label="Timeframe"
              onChange={(e) => setTimeframe(e.target.value)}
            >
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="quarter">This Quarter</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Project</InputLabel>
            <Select
              value={selectedProject}
              label="Project"
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <MenuItem value="all">All Projects</MenuItem>
              {projects.map(project => (
                <MenuItem key={project.id} value={project.id.toString()}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportReport}>
            Export Report
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={loadAnalyticsData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      {/* Key Metrics Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(6, 1fr)'
          },
          gap: 3,
          mb: 4
        }}
      >
        {getMetricCards().map((metric, index) => (
          <Card key={index}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    {metric.title}
                  </Typography>
                  <Typography variant="h5" component="div">
                    {metric.value}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    {metric.change > 0 ? (
                      <TrendingUpIcon color="success" fontSize="small" />
                    ) : (
                      <TrendingDownIcon color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      color={metric.change > 0 ? 'success.main' : 'error.main'}
                      sx={{ ml: 0.5 }}
                    >
                      {Math.abs(metric.change)}%
                    </Typography>
                  </Box>
                </Box>
                <Box color={`${metric.color}.main`}>
                  {metric.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '2fr 1fr'
          },
          gap: 3,
          mb: 3
        }}
      >
        {/* Project Performance Chart */}
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="between" sx={{ mb: 3 }}>
              <Typography variant="h6" component="h2">
                Project Performance Overview
              </Typography>
              <BarChartIcon color="action" />
            </Box>
            
            {/* Mock Chart Representation */}
            <Box sx={{ height: 300, display: 'flex', alignItems: 'end', gap: 2, p: 2 }}>
              {filteredProjects.map((project, index) => (
                <Box key={project.id} sx={{ flex: 1, textAlign: 'center' }}>
                  <Box
                    sx={{
                      height: `${(project.progress / 100) * 200}px`,
                      backgroundColor: `${getStatusColor(project.status)}.main`,
                      borderRadius: 1,
                      mb: 1,
                      minHeight: 20
                    }}
                  />
                  <Typography variant="caption" sx={{ transform: 'rotate(-45deg)', display: 'block' }}>
                    {project.name.length > 15 ? `${project.name.substring(0, 15)}...` : project.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {project.progress}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Top Performing Projects */}
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="between" sx={{ mb: 3 }}>
              <Typography variant="h6" component="h2">
                Top Performing Projects
              </Typography>
              <PieChartIcon color="action" />
            </Box>
            
            <List>
              {projects
                .sort((a, b) => b.clientRating - a.clientRating)
                .slice(0, 5)
                .map((project, index) => (
                  <React.Fragment key={project.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        {getStatusIcon(project.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={project.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Progress: {project.progress}% • Rating: {project.clientRating}/5
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={project.progress}
                              color={getStatusColor(project.status) as any}
                              sx={{ mt: 1, height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < 4 && <Divider />}
                  </React.Fragment>
                ))
              }
            </List>
          </CardContent>
        </Card>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)'
          },
          gap: 3
        }}
      >
        {/* Budget Analysis */}
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="between" sx={{ mb: 3 }}>
              <Typography variant="h6" component="h2">
                Budget Analysis
              </Typography>
              <MoneyIcon color="action" />
            </Box>
            
            <List>
              {filteredProjects.map((project) => {
                const budgetUsed = (project.spent / project.budget) * 100;
                const budgetColor = budgetUsed > 90 ? 'error' : budgetUsed > 75 ? 'warning' : 'success';
                
                return (
                  <React.Fragment key={project.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={project.name}
                        secondary={
                          <Box>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="body2">
                                {formatCurrency(project.spent)} / {formatCurrency(project.budget)}
                              </Typography>
                              <Typography variant="body2" color={`${budgetColor}.main`}>
                                {budgetUsed.toFixed(1)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={budgetUsed}
                              color={budgetColor as any}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </List>
          </CardContent>
        </Card>

        {/* Team Productivity */}
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="between" sx={{ mb: 3 }}>
              <Typography variant="h6" component="h2">
                Team Productivity
              </Typography>
              <PeopleIcon color="action" />
            </Box>
            
            <List>
              {filteredProjects.map((project) => {
                const taskCompletion = (project.tasksCompleted / project.totalTasks) * 100;
                const productivity = taskCompletion / project.teamSize;
                
                return (
                  <React.Fragment key={project.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={project.name}
                        secondary={
                          <Box>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="body2">
                                {project.tasksCompleted}/{project.totalTasks} tasks • {project.teamSize} members
                              </Typography>
                              <Chip 
                                label={`${productivity.toFixed(1)}% per member`}
                                size="small"
                                color={productivity > 15 ? 'success' : productivity > 10 ? 'warning' : 'error'}
                              />
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={taskCompletion}
                              color={taskCompletion > 80 ? 'success' : taskCompletion > 60 ? 'warning' : 'error'}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ProjectAnalytics;